import { useState } from 'react';
import { Sparkles, RefreshCw, ArrowRight } from 'lucide-react';
import { chAiService } from '../services/aiService';

interface Props {
  projectId: string;
  postId: string;
  title: string;
  currentCaption: string;
  clientComments: string[];
  onUseVersion: (caption: string, changeNote: string) => void;
}

export function AiFeedbackRevision({ projectId, postId, title, currentCaption, clientComments, onUseVersion }: Props) {
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<{ caption: string; change_note: string } | null>(null);
  const [editedCaption, setEditedCaption] = useState('');
  const [error, setError] = useState('');

  async function handleGenerate() {
    setGenerating(true);
    setError('');
    setResult(null);

    try {
      const res = await chAiService.feedbackRevision({
        project_id: projectId,
        post_id: postId,
        current_caption: currentCaption,
        client_comments: clientComments,
        title,
      });

      if (!res.success) {
        setError(res.error || 'Erro ao gerar revisão');
        return;
      }

      if (res.data) {
        setResult(res.data);
        setEditedCaption(res.data.caption);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao gerar revisão');
    } finally {
      setGenerating(false);
    }
  }

  if (!result && !generating && !error) {
    return (
      <button
        type="button"
        onClick={handleGenerate}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-amber-500 text-white hover:bg-amber-600 transition-colors"
      >
        <Sparkles className="w-3 h-3" />
        Gerar nova versão com base no feedback
      </button>
    );
  }

  return (
    <div className="mt-3 space-y-3 p-3 rounded-xl bg-white border border-gray-200">
      {error && (
        <div className="p-2 rounded-lg bg-red-50 text-red-700 text-xs">{error}</div>
      )}

      {generating && (
        <div className="flex items-center gap-2 py-3">
          <RefreshCw className="w-3.5 h-3.5 animate-spin text-gray-400" />
          <span className="text-xs text-gray-500">A gerar nova versão...</span>
        </div>
      )}

      {result && (
        <>
          {result.change_note && (
            <p className="text-[11px] text-gray-500 italic">
              Alterado: {result.change_note}
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Atual</p>
              <p className="text-xs text-gray-600 leading-relaxed">{currentCaption}</p>
            </div>
            <div className="p-2.5 rounded-lg bg-amber-50 border border-amber-100">
              <p className="text-[10px] font-bold text-amber-600 uppercase mb-1">Proposta IA</p>
              <textarea
                className="w-full text-xs text-gray-700 leading-relaxed bg-transparent border-none resize-none focus:outline-none"
                value={editedCaption}
                onChange={e => setEditedCaption(e.target.value)}
                rows={5}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onUseVersion(editedCaption, result.change_note)}
              className="ch-btn ch-btn-primary ch-btn-sm"
            >
              <ArrowRight className="w-3 h-3" />
              Usar esta versão
            </button>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generating}
              className="ch-btn ch-btn-ghost ch-btn-sm"
            >
              <RefreshCw className={`w-3 h-3 ${generating ? 'animate-spin' : ''}`} />
              Regenerar
            </button>
          </div>
        </>
      )}
    </div>
  );
}
